import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { SOCKET_URL, TOKEN_CHECK_INTERVAL } from '../utils/constants';
import { refreshToken } from '../utils/authUtils';

interface SocketContextType {
    socket: Socket | null;
    isConnected: boolean;
    refreshSocketToken: () => Promise<void>;
    updateSocketToken: () => void;
    forceUpdateSocketToken: () => void;
}

const SocketContext = createContext<SocketContextType>({
    socket: null,
    isConnected: false,
    refreshSocketToken: async () => { },
    updateSocketToken: () => { },
    forceUpdateSocketToken: () => { },
});

export const useSocket = () => useContext(SocketContext);

interface SocketProviderProps {
    children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
    const [socket, setSocket] = useState<Socket | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const [lastTokenRefresh, setLastTokenRefresh] = useState<number>(0);
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const currentTokenRef = useRef<string | null>(null);

    // Функция для обновления токена в сокете (только для автоматического обновления)
    const refreshSocketToken = useCallback(async () => {
        console.log('🔄 refreshSocketToken called (automatic)');
        const now = Date.now();

        // Проверяем, что прошло достаточно времени с последнего обновления
        if (now - lastTokenRefresh < TOKEN_CHECK_INTERVAL) {
            console.log('⏰ Token refresh throttled, skipping...');
            return;
        }

        // Дополнительная проверка - не обновляем токен если сокет не подключен
        if (!socket || !isConnected) {
            console.log('🔌 Socket not connected, skipping token refresh...');
            return;
        }

        try {
            console.log('🚀 Starting automatic token refresh...');
            // Обновляем токен через API
            const success = await refreshToken();
            if (success) {
                console.log('✅ Automatic token refresh completed');
                setLastTokenRefresh(now);
            } else {
                console.log('❌ Automatic token refresh failed');
            }
        } catch (error) {
            console.error('💥 Automatic token refresh failed:', error);
        }
    }, [socket, isConnected, lastTokenRefresh]);

    // Функция для ручного обновления токена в сокете
    const updateSocketToken = useCallback(() => {
        if (socket && isConnected) {
            const newToken = localStorage.getItem('jwt_token');
            if (newToken && newToken !== currentTokenRef.current) {
                console.log('🔄 Manually updating token in socket');
                currentTokenRef.current = newToken;
                socket.emit('refresh_token', newToken);
            } else {
                console.log('✅ Token unchanged or socket not ready');
            }
        } else {
            console.log('🔌 Socket not connected for manual update');
        }
    }, [socket, isConnected]);

    // Функция для принудительного обновления токена в сокете (без проверок)
    const forceUpdateSocketToken = useCallback(() => {
        if (socket && isConnected) {
            const newToken = localStorage.getItem('jwt_token');
            if (newToken) {
                console.log('🔄 Force updating token in socket');
                currentTokenRef.current = newToken;
                socket.emit('refresh_token', newToken);
            }
        }
    }, [socket, isConnected]);

    useEffect(() => {
        console.log('🔧 SocketContext useEffect triggered');
        let newSocket: Socket | null = null;
        let cleanup = () => { };

        const connectSocket = () => {
            console.log('🔧 connectSocket called');
            const token = localStorage.getItem('jwt_token');
            if (!token) {
                console.log('❌ No jwt_token, socket connection skipped.');
                setSocket(null);
                setIsConnected(false);
                return;
            }

            // Проверяем, изменился ли токен
            if (currentTokenRef.current === token && newSocket && isConnected) {
                console.log('✅ Token unchanged, skipping reconnection...');
                return;
            }

            console.log('🔄 Connecting socket with new token...');
            console.log('🔗 Socket URL:', SOCKET_URL);
            console.log('🔗 Socket path:', '/video-api/socket.io/');
            console.log('🔗 Full URL:', `${SOCKET_URL}/video-api/socket.io/`);
            currentTokenRef.current = token;

            if (newSocket) {
                newSocket.close();
            }

            newSocket = io(SOCKET_URL, {
                auth: {
                    token: token
                },
                path: '/video-api/socket.io/',
                transports: ['polling'],
                forceNew: true,
                timeout: 20000,
            });

            setSocket(newSocket);

            newSocket.on('connect', () => {
                console.log('🟢 Socket connected to:', SOCKET_URL);
                setIsConnected(true);
                // Инициализируем кеш токена при подключении
                const token = localStorage.getItem('jwt_token');
                if (token) {
                    currentTokenRef.current = token;
                }
            });

            newSocket.on('disconnect', (reason) => {
                console.log('🔴 Socket disconnected:', reason);
                setIsConnected(false);
            });

            newSocket.on('connect_error', (error) => {
                console.error('❌ Socket connection error:', error);
                setIsConnected(false);
            });

            // Слушаем ответ от сервера о обновлении токена
            newSocket.on('token_refreshed', (data) => {
                if (data.success) {
                    console.log('Token updated successfully');
                } else {
                    console.log('Token update failed');
                }
            });

            cleanup = () => {
                if (newSocket) newSocket.close();
            };
        };

        connectSocket();

        // ПОЛНОСТЬЮ ОТКЛЮЧАЕМ АВТОМАТИЧЕСКОЕ ОБНОВЛЕНИЕ ТОКЕНА В СОКЕТЕ
        // const handleTokenUpdate = () => {
        //     console.log('🎯 token:updated event received');

        //     // Если сокет подключен, просто обновляем токен в нем
        //     if (socket && isConnected) {
        //         const newToken = localStorage.getItem('jwt_token');
        //         if (newToken && newToken !== currentTokenRef.current) {
        //             console.log('🔄 Updating token in existing socket connection');
        //             currentTokenRef.current = newToken;
        //             socket.emit('refresh_token', newToken);
        //         } else {
        //             console.log('✅ Token unchanged or socket not ready, skipping update');
        //         }
        //     } else {
        //         // Если сокет не подключен, подключаем его
        //         console.log('🔌 Socket not connected, connecting...');
        //         connectSocket();
        //     }
        // };
        // window.addEventListener('token:updated', handleTokenUpdate);

        return () => {
            // window.removeEventListener('token:updated', handleTokenUpdate);
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
            cleanup();
        };
    }, []);

    // ОТКЛЮЧАЕМ АВТОМАТИЧЕСКОЕ ОБНОВЛЕНИЕ ТОКЕНА
    // useEffect(() => {
    //     let tokenRefreshInterval: NodeJS.Timeout | null = null;

    //     if (socket && isConnected) {
    //         console.log('⏰ Starting automatic token refresh interval');
    //         tokenRefreshInterval = setInterval(refreshSocketToken, TOKEN_CHECK_INTERVAL);
    //     }

    //     return () => {
    //         if (tokenRefreshInterval) {
    //             console.log('⏰ Stopping automatic token refresh interval');
    //             clearInterval(tokenRefreshInterval);
    //         }
    //     };
    // }, [socket, isConnected, refreshSocketToken]);

    return (
        <SocketContext.Provider value={{ socket, isConnected, refreshSocketToken, updateSocketToken, forceUpdateSocketToken }}>
            {children}
        </SocketContext.Provider>
    );
}; 